import {
  ASSUMED_LEGACY_FILED_PASSENGER_RATES,
  computeF720RetrospectiveByQuarter,
  computeF720RetrospectiveByYear,
  IRS_INDEXED_PASSENGER_AIR_BY_YEAR,
} from "./f720Retrospective";
import { money } from "./money";
import type { FlightLeg, Rates } from "./types";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fmtUsd(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function quarterTableRows(legs: FlightLeg[], rates: Rates): string {
  const { rows, unknownYearLegCount } = computeF720RetrospectiveByQuarter(legs, rates);
  const totalDelta = money(rows.reduce((a, r) => a + r.deltaAirExcise, 0));

  const body =
    rows.length === 0
      ? `<tr><td colspan="15" class="muted-small">No legs in <strong>${Object.keys(IRS_INDEXED_PASSENGER_AIR_BY_YEAR).join(", ")}</strong> (by calendar quarter). Load flights in those years, or extend <code>IRS_INDEXED_PASSENGER_AIR_BY_YEAR</code> in <code>f720Retrospective.ts</code>.</td></tr>`
      : rows
          .map(
            (r) => `<tr>
    <td><strong>${escapeHtml(r.periodLabel)}</strong></td>
    <td class="comparison-num">${r.legCount}</td>
    <td class="comparison-num">${fmtUsd(r.legacyDomesticSegment)}</td>
    <td class="comparison-num">${fmtUsd(r.officialDomesticSegment)}</td>
    <td class="comparison-num">${fmtUsd(r.deltaDomesticSegment)}</td>
    <td class="comparison-num">${fmtUsd(r.legacyIntlFacility)}</td>
    <td class="comparison-num">${fmtUsd(r.officialIntlFacility)}</td>
    <td class="comparison-num">${fmtUsd(r.deltaIntlFacility)}</td>
    <td class="comparison-num">${fmtUsd(r.legacyAkHiDeparture)}</td>
    <td class="comparison-num">${fmtUsd(r.officialAkHiDeparture)}</td>
    <td class="comparison-num">${fmtUsd(r.deltaAkHiDeparture)}</td>
    <td class="comparison-num">${fmtUsd(r.legacyAirExcise)}</td>
    <td class="comparison-num">${fmtUsd(r.officialAirExcise)}</td>
    <td class="comparison-num"><strong>${fmtUsd(r.deltaAirExcise)}</strong></td>
    <td class="muted-small comparison-note">${escapeHtml(r.citation)}</td>
  </tr>`
          )
          .join("");

  const unknownNote =
    unknownYearLegCount > 0
      ? `<p class="sheets-hint muted-small" role="note"><strong>${unknownYearLegCount}</strong> leg(s) skipped (bad date or calendar year not in the IRS indexed table).</p>`
      : "";

  return `
      <p class="panel-lead">
        One row per <strong>calendar quarter</strong> (by flight date). <strong>Legacy</strong> columns use a fixed worksheet assumption:
        <strong>$${ASSUMED_LEGACY_FILED_PASSENGER_RATES.domesticSegmentUsdPerPassengerPerSegment.toFixed(2)}</strong>/segment (domestic),
        <strong>$${ASSUMED_LEGACY_FILED_PASSENGER_RATES.internationalFacilityUsdPerPerson.toFixed(2)}</strong>/person (intl facility),
        <strong>$${ASSUMED_LEGACY_FILED_PASSENGER_RATES.alaskaHawaiiDomesticDepartureUsdPerPerson.toFixed(2)}</strong>/person (AK-HI departure).
        <strong>IRS CY</strong> columns use the <strong>indexed amounts for that leg’s calendar year</strong> (see Rev. Proc. in the last column).
        For <strong>2023</strong>, IRS domestic and intl match those legacy numbers exactly, but <strong>AK-HI</strong> does not ($10.60 indexed vs $11.70 assumed)—so you can see <strong>Δ total</strong> move even when <strong>Δ Dom.</strong> and <strong>Δ Intl</strong> are $0.
      </p>
      <p class="sheets-hint muted-small">
        <strong>Total @ legacy / IRS CY</strong> is full air excise on the leg (same as elsewhere in the app): <strong>7.5% × charter fare</strong> (identical in both columns—only passenger lines change) <strong>plus</strong> domestic segment + intl facility + AK-HI departure. So
        <strong>Δ total = Δ Dom. + Δ Intl + Δ AK-HI</strong> each quarter (charter drops out).
        Positive <strong>Δ total</strong> means indexed passenger rates imply <em>more</em> tax than the legacy assumption; negative means <em>less</em> (illustrative only).
      </p>
      <p class="sheets-hint muted-small">
        <strong>Charter %</strong> uses the current <strong>Tax rate</strong> on the Form 720 tab for both total columns. Legs here match Form 720: <strong>personal-classified only</strong> (business legs are excluded from this table).
      </p>
      ${unknownNote}
      <div class="comparison-table-wrap">
        <table class="totals comparison-table comparison-breakdown-table">
          <thead>
            <tr>
              <th scope="col">Quarter</th>
              <th scope="col">Legs</th>
              <th scope="col" title="IRS No. 26 domestic segment at assumed worksheet rate">Dom. @ legacy</th>
              <th scope="col" title="IRS No. 26 domestic segment at indexed CY rate">Dom. @ IRS CY</th>
              <th scope="col">Δ Dom.</th>
              <th scope="col" title="IRS No. 27 at assumed worksheet rate">Intl @ legacy</th>
              <th scope="col" title="IRS No. 27 at indexed CY rate">Intl @ IRS CY</th>
              <th scope="col">Δ Intl</th>
              <th scope="col" title="AK/HI domestic departure at assumed $11.70/person">AK-HI @ legacy</th>
              <th scope="col" title="AK/HI domestic departure at indexed CY rate">AK-HI @ IRS CY</th>
              <th scope="col">Δ AK-HI</th>
              <th scope="col" title="Charter % + Dom + Intl + AK-HI">Total @ legacy</th>
              <th scope="col" title="Charter % + Dom + Intl + AK-HI">Total @ IRS CY</th>
              <th scope="col">Δ total</th>
              <th scope="col">IRS source</th>
            </tr>
          </thead>
          <tbody>
            ${body}
            ${
              rows.length > 0
                ? `<tr class="comparison-match">
              <th scope="row" colspan="13"><strong>Sum of Δ total</strong> (all quarter rows above)</th>
              <td class="comparison-num"><strong>${fmtUsd(totalDelta)}</strong></td>
              <td class="comparison-note muted-small"></td>
            </tr>`
                : ""
            }
          </tbody>
        </table>
      </div>`;
}

function yearRollupHtml(legs: FlightLeg[], rates: Rates): string {
  const { rows } = computeF720RetrospectiveByYear(legs, rates);
  if (rows.length === 0) return "";

  const body = rows
    .map(
      (r) => `<tr>
    <td><strong>${r.year}</strong></td>
    <td class="comparison-num">${r.legCount}</td>
    <td class="comparison-num">${fmtUsd(r.deltaAirExcise)}</td>
  </tr>`
    )
    .join("");

  return `
    <details class="panel panel-tight">
      <summary class="help-how-summary">Calendar-year roll-up (same math, one row per year)</summary>
      <p class="sheets-hint muted-small">Totals match summing the quarter rows above within each year.</p>
      <div class="comparison-table-wrap">
        <table class="totals comparison-table">
          <thead>
            <tr>
              <th scope="col">Year</th>
              <th scope="col">Legs</th>
              <th scope="col">Δ total air excise</th>
            </tr>
          </thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    </details>`;
}

/** HTML for <strong>F720 retrospective</strong> tab. */
export function f720RetrospectivePanelHtml(legs: FlightLeg[], rates: Rates): string {
  return `
    <section class="panel">
      <h2 class="tab-panel-heading">F720 rate retrospective</h2>
      ${quarterTableRows(legs, rates)}
    </section>
    ${yearRollupHtml(legs, rates)}`;
}

export function updateF720RetrospectivePanel(root: HTMLElement, legs: FlightLeg[], rates: Rates): void {
  const el = root.querySelector<HTMLElement>("#f720-retro-root");
  if (!el) return;
  el.innerHTML = f720RetrospectivePanelHtml(legs, rates);
}
